import {
  asClause,
  fillFrame,
  lowerFirst,
  properWords,
  stripEnd,
} from "@/lib/tool-kit/essay/sentence";

/**
 * Toulmin arguments: claim, grounds, warrant, backing, qualifier, rebuttal.
 *
 * The six parts are Stephen Toulmin's, from *The Uses of Argument* (1958). Three of them
 * carry the argument (claim, grounds, warrant) and three refine it (backing, qualifier,
 * rebuttal), so only the first three are required: a paragraph built without a backing
 * simply has no backing sentence.
 *
 * The qualifier is placed for the reader in two shapes. At the start of a sentence it
 * takes a comma — "In most lecture courses, students should…" — and after a lead-in it
 * does not: "Therefore, presumably Harry is a British subject."
 */

export type ToulminField =
  | "claim"
  | "qualifier"
  | "grounds"
  | "warrant"
  | "backing"
  | "rebuttal";

export type ToulminInput = Record<ToulminField, string>;

export type ToulminPart =
  | "grounds"
  | "claim"
  | "groundsClaim"
  | "warrant"
  | "backing"
  | "rebuttal";

export type ToulminVersion = "dataFirst" | "claimFirst" | "givenThat";

export const TOULMIN_VERSIONS: readonly ToulminVersion[] = [
  "dataFirst",
  "claimFirst",
  "givenThat",
];

/** The part each frame position fills, per wording. */
export const TOULMIN_ORDER: Record<ToulminVersion, readonly ToulminPart[]> = {
  dataFirst: ["grounds", "claim", "warrant", "backing", "rebuttal"],
  claimFirst: ["claim", "grounds", "warrant", "backing", "rebuttal"],
  givenThat: ["groundsClaim", "warrant", "backing", "rebuttal"],
};

/**
 * English frames. `{qualified}` is the claim with a sentence-opening qualifier and its
 * comma; `{qualifiedInline}` is the same claim with the qualifier run in.
 */
export const TOULMIN_FRAMES = {
  dataFirst: [
    "{grounds}.",
    "Therefore, {qualifiedInline}.",
    "This conclusion follows because {warrant}.",
    "That principle is well supported, since {backing}.",
    "The conclusion holds unless {rebuttal}.",
  ],
  claimFirst: [
    "{qualified}.",
    "The main evidence is that {grounds}.",
    "That evidence matters because {warrant}.",
    "This assumption is backed by the fact that {backing}.",
    "The claim would not apply, however, if {rebuttal}.",
  ],
  givenThat: [
    "Given that {grounds}, {qualifiedInline}.",
    "The reasoning is that {warrant}.",
    "In support of this, {backing}.",
    "An exception would be if {rebuttal}.",
  ],
} as const;

/** Qualifiers a reader can drop in with one click. */
export const TOULMIN_QUALIFIERS = [
  "probably",
  "almost certainly",
  "in most cases",
  "presumably",
  "usually",
  "very likely",
] as const;

const REQUIRED: readonly ToulminField[] = ["claim", "grounds", "warrant"];

export function missingToulminFields(input: ToulminInput): ToulminField[] {
  return REQUIRED.filter((field) => input[field].trim() === "");
}

function optionalClause(
  text: string,
  proper: ReadonlySet<string>
): string | null {
  return text.trim() === "" ? null : asClause(text, proper);
}

/** The values every frame draws on, each shaped to sit inside a sentence. */
export function toulminValues(
  input: ToulminInput
): Record<string, string | null> {
  const proper = properWords(Object.values(input));
  const claim = asClause(input.claim, proper);
  const qualifier = lowerFirst(stripEnd(input.qualifier), proper);
  return {
    claim,
    qualified: qualifier === "" ? claim : `${qualifier}, ${claim}`,
    qualifiedInline: qualifier === "" ? claim : `${qualifier} ${claim}`,
    grounds: asClause(input.grounds, proper),
    warrant: asClause(input.warrant, proper),
    backing: optionalClause(input.backing, proper),
    rebuttal: optionalClause(input.rebuttal, proper),
  };
}

export interface ToulminSentence {
  part: ToulminPart;
  text: string;
}

/** Every wording, each as labelled sentences. Optional parts left empty drop out. */
export function buildToulminParagraphs(
  input: ToulminInput,
  frames: Readonly<Record<string, readonly string[]>> = TOULMIN_FRAMES
): ToulminSentence[][] {
  const values = toulminValues(input);
  return TOULMIN_VERSIONS.map((version) => {
    const order = TOULMIN_ORDER[version];
    const sentences: ToulminSentence[] = [];
    for (const [index, template] of (frames[version] ?? []).entries()) {
      const part = order[index];
      const text = part ? fillFrame(template, values) : null;
      if (part && text) {
        sentences.push({ part, text });
      }
    }
    return sentences;
  });
}
